import { PluggableStorage, SignInMethod } from '@frontend/auth-helpers';
import { metricsLogger } from '@frontend/tracking';
import type { AuthAttemptsBucket } from './auth-flow-timer';

/**
 * `existing_session` is the auto-accept path: the user already has a valid session, so no credentials
 * are entered. It's tracked so accept-flow failures on that path are visible, but filter it out when
 * looking at credential success rates.
 */
export type AuthMethod = SignInMethod | 'password' | 'webauthn' | 'existing_session';
/** `cancelled` is a user-initiated abort (e.g. dismissing the passkey prompt) and shouldn't count against the success rate. */
export type AuthOutcome = 'success' | 'fail' | 'cancelled';

const TEAM = 'users-infra';
const CATEGORY = 'auth';

/**
 * Durations longer than this are dropped rather than reported. A genuine auth flow (including IdP login
 * and MFA) doesn't take this long; anything beyond it is a stale start time (orphaned storage entry, a
 * tab left open overnight, laptop sleep, clock change) and would wreck the p90+.
 */
export const MAX_TIME_TO_AUTH_MS = 10 * 60 * 1000;

/**
 * Where an auth flow came from, sent as the `ref` attribute on every auth metric:
 * - `mobile`: the OAuth client is one of the known mobile app client ids
 * - `weave-desktop` (and any other known ref): the `ref` the originating app put in the OAuth `state` of its
 *   authorize URL, passed through as-is
 * - `other`: a `ref` that isn't in `KNOWN_REFS`. `state` comes from a URL, so unrecognized values are
 *   collapsed rather than passed through, to keep the attribute to a fixed set of values
 * - `none`: no `ref` and not a mobile client, e.g. the web app
 */
export type AuthFlowRef = 'mobile' | 'other' | 'none' | (typeof KNOWN_REFS)[number];

/** refs that are passed through as-is. Add to this when another app starts sending a `ref`. */
const KNOWN_REFS = ['weave-desktop'] as const;
// Persists through the IdP redirect, which reloads Gatekeeper, while remaining scoped to the current tab.
const flowOriginStore = PluggableStorage.create('auth-metrics', sessionStorage);
const FLOW_ORIGIN_KEY = 'flow-origin';

interface StoredFlowOrigin {
  challenge: string;
  ref: AuthFlowRef;
}

/** The origin of the current auth flow. Undefined until the flow has been initialized. */
let authFlowRef: AuthFlowRef | undefined;

function isAuthFlowRef(value: unknown): value is AuthFlowRef {
  return (
    value === 'mobile' || value === 'other' || value === 'none' || (KNOWN_REFS as readonly unknown[]).includes(value)
  );
}

function resolveAuthFlowRef(ref: string | undefined, isMobileApp: boolean): AuthFlowRef {
  if (isMobileApp) {
    return 'mobile';
  }
  if (!ref) {
    return 'none';
  }
  return isAuthFlowRef(ref) && ref !== 'mobile' && ref !== 'none' ? ref : 'other';
}

interface SetAuthFlowOriginArgs {
  /** the `ref` from the authorize URL's `state` */
  ref: string | undefined;
  /** the OAuth client is a known mobile app client id */
  isMobileApp: boolean;
  /** the flow's login challenge, which the stored value is tied to */
  challenge: string;
}

/**
 * Record where the current auth flow came from. Call once the login flow has been initialized.
 * This is used for analytics purposes.
 *
 * It's also persisted against the flow's login challenge so the callback pages, which load after a full-page
 * redirect to an IdP, can pick it up with `restoreAuthFlowOrigin`.
 */
export function setAuthFlowOrigin({ ref, isMobileApp, challenge }: SetAuthFlowOriginArgs): void {
  authFlowRef = resolveAuthFlowRef(ref, isMobileApp);
  if (!challenge) {
    return;
  }
  try {
    const value: StoredFlowOrigin = { challenge, ref: authFlowRef };
    flowOriginStore.setItem(FLOW_ORIGIN_KEY, value);
  } catch {
    // storage may be unavailable (e.g. blocked by browser settings); the attribute is best-effort
  }
}

/**
 * Restore the flow origin on a page reached by a full-page redirect. The stored value is only used if it was
 * recorded for the same login challenge, so one left behind by an earlier flow in this tab is ignored.
 */
export function restoreAuthFlowOrigin(challenge: string): void {
  try {
    const storedOrigin = flowOriginStore.getItem<StoredFlowOrigin>(FLOW_ORIGIN_KEY);
    if (!storedOrigin || !challenge) {
      return;
    }
    if (storedOrigin.challenge === challenge && isAuthFlowRef(storedOrigin.ref)) {
      authFlowRef = storedOrigin.ref;
    }
  } catch {
    // see setAuthFlowOrigin
  }
}

/** `ref` is added to every auth metric once the flow's origin is known, and omitted until then. */
function getFlowOriginAttributes(): { ref?: AuthFlowRef } {
  return authFlowRef === undefined ? {} : { ref: authFlowRef };
}

interface RecordAuthResultArgs {
  method: AuthMethod;
  outcome: AuthOutcome;
  failureReason?: string;
}

/** Counter for auth attempts. Slice by `method` for the method distribution and by `outcome` for success/fail rate. */
export function recordAuthResult({ method, outcome, failureReason }: RecordAuthResultArgs): void {
  try {
    metricsLogger.metric('auth.result', 1, {
      attributes: {
        team: TEAM,
        category: CATEGORY,
        auth_method: method, // "method" maps to "http.request.method" in Sentry land, so rename it
        outcome,
        ...(failureReason ? { failure_reason: failureReason } : {}),
        ...getFlowOriginAttributes(),
      },
    });
  } catch (err) {
    console.error('Failed to record auth.result metric', err);
  }
}

interface RecordTimeToAuthArgs {
  durationMs: number | undefined;
  method: AuthMethod;
  outcome: AuthOutcome;
  /** whether the flow included an MFA code step, which adds human input time */
  mfa?: boolean;
  /** how many credential attempts it took (see AuthFlowTimer) */
  attempts?: AuthAttemptsBucket;
  /** the tab was hidden at some point during the flow */
  backgrounded?: boolean;
  /** the user went idle at some point; the idle time itself is excluded from the duration */
  idle?: boolean;
}

/**
 * Distribution of end-to-end auth duration in ms, sliced by `method` and `outcome`. Missing, negative, or
 * implausibly long durations (see `MAX_TIME_TO_AUTH_MS`) are dropped.
 */
export function recordTimeToAuth({
  durationMs,
  method,
  outcome,
  mfa,
  attempts,
  backgrounded,
  idle,
}: RecordTimeToAuthArgs): void {
  if (!isPlausibleDuration(durationMs)) {
    return;
  }
  try {
    metricsLogger.timing('auth.time_to_auth', Math.round(durationMs), {
      attributes: {
        team: TEAM,
        category: CATEGORY,
        auth_method: method,
        outcome,
        ...(mfa !== undefined ? { mfa } : {}),
        ...(attempts !== undefined ? { attempts } : {}),
        ...(backgrounded !== undefined ? { backgrounded } : {}),
        ...(idle !== undefined ? { idle } : {}),
        ...getFlowOriginAttributes(),
      },
    });
  } catch (err) {
    console.error('Failed to record auth.time_to_auth metric', err);
  }
}

function isPlausibleDuration(durationMs: number | undefined): durationMs is number {
  return (
    durationMs !== undefined && Number.isFinite(durationMs) && durationMs >= 0 && durationMs <= MAX_TIME_TO_AUTH_MS
  );
}

type CountAttributes = Record<string, string | number | boolean>;

/** Shared counter emit. Metrics must never break the flow they're instrumenting, so failures are swallowed. */
function recordCount(name: string, attributes: CountAttributes): void {
  try {
    metricsLogger.metric(name, 1, {
      attributes: { team: TEAM, category: CATEGORY, ...attributes, ...getFlowOriginAttributes() },
    });
  } catch (err) {
    console.error(`Failed to record ${name} metric`, err);
  }
}

export type MfaMethod = 'email' | 'sms' | 'unknown';
/**
 * - `verified`: the user enabled MFA from the prompt
 * - `skipped`: the user dismissed the prompt and will see it again
 * - `opted_out`: the user dismissed the prompt with "Don't show this again" checked
 */
export type MfaPromptOutcome = 'verified' | 'skipped' | 'opted_out';

/** Counter for how the post-login MFA prompt ended, including the method that was verified when applicable. */
export function recordMfaPromptResult(outcome: MfaPromptOutcome, mfaMethod?: MfaMethod): void {
  recordCount('auth.mfa_prompt.result', { outcome, ...(mfaMethod ? { mfa_method: mfaMethod } : {}) });
}

/** Maps the auth-api contact/method type (`EMAIL` | `PHONE`) to the `mfa_method` attribute value. */
export function toMfaMethod(type: string | undefined): MfaMethod {
  switch (type) {
    case 'EMAIL':
      return 'email';
    case 'PHONE':
      return 'sms';
    default:
      return 'unknown';
  }
}

/**
 * Whether the lockout message has been shown since this page loaded. Kept in memory rather than in storage
 * so it can't go stale: it survives the client-side navigation to /reset-password and nothing else.
 */
let hasBeenLockedOut = false;

/**
 * Counter for a user hitting the lockout message after repeated failed sign-ins. Recorded once per page
 * load, so it counts people rather than every further failed attempt.
 */
export function recordLockout(): void {
  if (hasBeenLockedOut) {
    return;
  }
  hasBeenLockedOut = true;
  recordCount('auth.lockout', {});
}

/** What led the user to the reset flow. `forgot_password` is the user choosing it without being locked out. */
export type PasswordResetTrigger = 'lockout' | 'pwned_password' | 'forgot_password';

export function getPasswordResetTrigger(errorCode?: string): PasswordResetTrigger {
  if (errorCode === 'pwnedPassword') {
    return 'pwned_password';
  }
  return hasBeenLockedOut ? 'lockout' : 'forgot_password';
}

/** Counter for successful password reset requests, sliced by what brought the user to the reset flow. */
export function recordPasswordReset(trigger: PasswordResetTrigger): void {
  recordCount('auth.password_reset', { trigger });
}

/** test-only: clears the in-memory lockout flag and flow origin */
export function resetAuthMetricsStateForTests(): void {
  hasBeenLockedOut = false;
  authFlowRef = undefined;
}
